/**
 * OsintProgressPanel — live panel shown while an OSINT investigation runs.
 * Subscribes to the run's SSE stream and renders the register checklist
 * (update_plan → `plan` events) plus a compact activity line, mirroring the
 * deep-research RunningProgressPanel so the two surfaces feel identical.
 */

import { Globe, Loader2, Search } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { coercePlanTodos, RunPlanList } from '@/components/hypotheses/RunPlanList'
import { Card } from '@/components/ui/card'
import { useElapsedLabel } from '@/hooks/use-elapsed-label'
import type { HypothesisRunTodo } from '@/types/hypothesis-finding'
import { useOsintRunEvents } from './hooks/use-osint-events'

interface Props {
  caseId: string
  osintId: string
  startedAt?: string | null
}

export function OsintProgressPanel({ caseId, osintId, startedAt }: Props) {
  const { t } = useTranslation()
  const { events, isConnected } = useOsintRunEvents(caseId, osintId)
  const elapsed = useElapsedLabel(startedAt ?? null)

  // Latest register checklist — the agent maintains it via update_plan; only
  // the last `plan` event matters (the SSE endpoint replays it on connect).
  const todos = useMemo<HypothesisRunTodo[]>(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]
      if (e.type === 'plan') return coercePlanTodos((e as { todos?: unknown }).todos)
    }
    return []
  }, [events])

  // Researcher web lookups arrive as `subagent_activity` events — one per
  // web_search a researcher runs; their count is the live "searches" signal.
  const searchCount = useMemo(
    () => events.filter(e => e.type === 'subagent_activity').length,
    [events]
  )

  return (
    <Card tone="primary" className="flex flex-col gap-4 p-4 md:p-5">
      <div className="flex items-center gap-3">
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
        <div className="flex flex-1 items-center justify-between gap-3 text-xs">
          <span className="font-medium text-foreground">
            {isConnected ? t('osint.progress.researching') : t('osint.progress.reconnecting')}
          </span>
          {elapsed && (
            <span className="font-mono tabular-nums text-muted-foreground">{elapsed}</span>
          )}
        </div>
      </div>

      {todos.length > 0 ? (
        <RunPlanList todos={todos} />
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Globe className="size-3" />
          {t('osint.progress.planning')}
        </div>
      )}

      {searchCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Search className="size-3" />
          {t('osint.progress.searches', { count: searchCount })}
        </div>
      )}
    </Card>
  )
}
